import React from "react";

/**
 * Returns a copy of `value` that updates at most once per animation frame,
 * always settling on the latest value (trailing edge).
 *
 * During a zoom gesture the wheel/pointer handlers can fire several times per
 * frame, each producing a new map transform. The live transform should follow
 * every one of those (so the map feels responsive), but the derived work that
 * hangs off it — culling the SVG overlay and picking which tiles to render —
 * doesn't need to recompute more than once per painted frame. Feeding that
 * derived work this throttled value coalesces those bursts into a single
 * recompute per frame without ever leaving stale culling behind.
 */
export const useRafThrottledValue = <T>(value: T): T => {
  const [throttled, setThrottled] = React.useState(value);
  const rafRef = React.useRef<number | undefined>(undefined);
  const latestRef = React.useRef(value);
  latestRef.current = value;

  React.useEffect(() => {
    // Already caught up, or a flush is already scheduled for this frame.
    if (throttled === value || rafRef.current !== undefined) {
      return;
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = undefined;
      setThrottled(latestRef.current);
    });
  }, [value, throttled]);

  React.useEffect(
    () => () => {
      if (rafRef.current !== undefined) {
        cancelAnimationFrame(rafRef.current);
      }
    },
    []
  );

  return throttled;
};
